import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest, getApiUrl } from '../../config/api';
import SuccessToast from '../SuccessToast';
import LegalCartaMarco from './LegalCartaMarco';
import { etiquetaBotonCertificado, etiquetaTipoCuenta, mensajeContratoFirmado, parrafosDocumento } from './legalLabels';
import LegalOnboardingFlow from './LegalOnboardingFlow';
import LegalDocumentViewer from './LegalDocumentViewer';
import './legal.css';

const ETIQUETAS_JUSTIFICANTE = new Set(['Firmante', 'Documento', 'Aceptación']);

function fechaAceptacion(status) {
  const primero = (status.acceptedDocuments || [])[0];
  return primero?.acceptedAt || null;
}

function bloqueJustificante(parrafo, indice) {
  const lineas = parrafo.split('\n');
  if (ETIQUETAS_JUSTIFICANTE.has(lineas[0]) && lineas.length > 1) {
    return (
      <div key={indice}>
        <p className="legal-sheet-etiqueta">{lineas[0]}</p>
        <p className="legal-sheet-datos">{lineas.slice(1).join('\n')}</p>
      </div>
    );
  }
  if (indice === 0) {
    return <h3 key={indice} className="legal-sheet-titulo">{parrafo}</h3>;
  }
  return <p key={indice} className="legal-sheet-cuerpo">{parrafo}</p>;
}

function LegalContractSection({ botId }) {
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [certificate, setCertificate] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const cargaId = useRef(0);
  const loadStatus = useCallback(() => {
    if (botId == null) return;
    const id = cargaId.current + 1;
    cargaId.current = id;
    setError('');
    setStatus(null);
    setCertificate('');
    apiRequest(getApiUrl(`/legal/onboarding/status?botId=${botId}`))
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo cargar el contrato');
        return res.json();
      })
      .then((data) => {
        if (id === cargaId.current) setStatus(data);
      })
      .catch(() => {
        if (id === cargaId.current) setError('No se pudo cargar el contrato');
      });
  }, [botId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!status?.completed || botId == null) {
      setDocuments([]);
      return;
    }
    apiRequest(getApiUrl(`/legal/onboarding/documents?botId=${botId}`))
      .then((res) => (res.ok ? res.json() : []))
      .then(setDocuments)
      .catch(() => setDocuments([]));
  }, [status, botId]);

  const verCertificado = (acceptanceId) => {
    setError('');
    apiRequest(getApiUrl(`/legal/onboarding/certificate/${acceptanceId}?botId=${botId}`))
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo cargar la constancia');
        return res.text();
      })
      .then(setCertificate)
      .catch(() => setError('No se pudo cargar la constancia'));
  };

  const firmarTerminado = (emailSent) => {
    setToast(mensajeContratoFirmado(emailSent));
    loadStatus();
  };

  const reenviarCorreo = () => {
    setError('');
    apiRequest(getApiUrl('/legal/onboarding/resend-email'), {
      method: 'POST',
      body: JSON.stringify({ botId }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo reenviar el correo');
        setToast('Correo enviado');
      })
      .catch(() => setError('No se pudo reenviar el correo'));
  };

  const conToast = (contenido) => (
    <>
      {contenido}
      <SuccessToast message={toast} onDismiss={() => setToast(null)} />
    </>
  );

  if (botId == null) {
    return (
      <div className="registros-placeholder">
        <h2>Contrato</h2>
        <p>Selecciona un bot en la cabecera.</p>
      </div>
    );
  }

  if (error && !status) {
    return conToast(
      <div className="legal-mesa">
        <div className="legal-banner-error">{error}</div>
      </div>
    );
  }

  if (!status) {
    return conToast(
      <div className="registros-placeholder">
        <h2>Contrato</h2>
        <p>Cargando…</p>
      </div>
    );
  }

  if (!status.completed) {
    return conToast(
      <LegalOnboardingFlow
        botId={botId}
        reanudarPerfilId={status.currentProfileId}
        reanudarTipo={status.tenantType}
        onComplete={firmarTerminado}
      />
    );
  }

  const firmante = status.signerName || `Usuario ${status.signerUserId || ''}`;
  const fecha = fechaAceptacion(status);
  const aceptaciones = status.acceptedDocuments || [];

  return conToast(
    <div className="legal-mesa legal-contract-consultation">
      {error && <div className="legal-banner-error">{error}</div>}
      <header className="legal-consulta-franja">
        <p className="legal-consulta-estado">Contrato aceptado</p>
        <dl>
          <div>
            <dt>Tipo</dt>
            <dd>{etiquetaTipoCuenta(status.tenantType)}</dd>
          </div>
          <div>
            <dt>Firmante</dt>
            <dd>{firmante}</dd>
          </div>
          {fecha && (
            <div>
              <dt>Fecha</dt>
              <dd>{fecha}</dd>
            </div>
          )}
        </dl>
      </header>

      {documents.map((doc) => (
        <LegalDocumentViewer key={doc.versionId} document={doc} soloLectura />
      ))}

      <button type="button" className="legal-btn-secondary" onClick={reenviarCorreo}>
        Reenviar al correo
      </button>

      {aceptaciones.map((acceptance) => (
        <button
          key={acceptance.acceptanceId}
          type="button"
          className="legal-btn-secondary"
          onClick={() => verCertificado(acceptance.acceptanceId)}
        >
          {etiquetaBotonCertificado(acceptance, documents)}
        </button>
      ))}

      {certificate && (
        <LegalCartaMarco marcaAgua>
          {parrafosDocumento(certificate)
            .filter((parrafo) => parrafo !== 'Botomatic')
            .map((parrafo, indice) => bloqueJustificante(parrafo, indice))}
        </LegalCartaMarco>
      )}
    </div>
  );
}

export default LegalContractSection;
